/* eslint-disable max-lines */
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { Dispatch } from 'redux';
import { connect } from 'react-redux';
import { RootState } from 'typesafe-actions';
import { ConnectedProps } from 'types/utils';
import { ProductCard } from 'common/components';
import { useDyDataCollect, useIntersectionObserverCallback } from 'common/hooks';
import { memo } from 'common/hocs/memo';
import { BasketFlowActions, getBasket } from 'features/basket';
import { dispatchDyAddToCart } from 'utils/dynamic-yields';
import { analyticsUtils } from 'utils/analytics-utils';
import { ProductOwnProps } from './product.types';
import { clickRRTracking, labelsGaNames, updateGFData, viewRRTracking } from './utils';
import { useOnScreen } from 'common/hooks/use-on-screen';

const EVENT = 'basket:update';

export type ProductLogicProps = ProductOwnProps &
  ConnectedProps<typeof mapStateToProps, typeof mapDispatchToProps>;
export const ProductLogic = memo<ProductLogicProps>(function ProductLogic({
  className,
  type = 'large-card',
  elementBlock = 'not set',
  product,
  inBasket,
  qty = 0,
  productPosition,
  addToBasket,
  removeFromBasket,
  syncCartWithCookie,
  reviews,
  updateCartItem,
  inWidget,
  rrContentId = '',
}) {
  const productRef = useRef<HTMLDivElement | null>(null);
  const prevQtyRef = useRef<number>(qty);
  const isOnScreen = useOnScreen(productRef, {
    threshold: 1,
  });

  useEffect(() => {
    prevQtyRef.current = qty;
  }, [qty]);
  const label = useMemo(
    () => (product.source.toUpperCase() === 'PIM' ? 'PIM' : product.productPriceCategory),
    [product.productPriceCategory, product.source]
  );
  const review = useMemo(
    () => reviews?.find(item => item.productId?.toString() === product.productId) ?? null,
    [product.productId, reviews]
  );
  const gfData = useDyDataCollect(productRef);
  useEffect(() => {
    const cb = () => {
      syncCartWithCookie();
    };
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    !__IS_SERVER__ && document.body.addEventListener(EVENT, cb, { passive: true });
    return () => {
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      !__IS_SERVER__ && document.body.removeEventListener(EVENT, cb);
    };
  }, [syncCartWithCookie]);

  const commonGaData = useMemo(
    () => ({
      elementBlock,
      elementType: 'product',
      productName: product.displayedName,
      productId: product.productId,
      productPrice: product.price.main_price,
      productPosition,
      productPrevPrice: product.price.previous_price ?? null,
      abTestPrice: product.price.previous_price ? '1' : null,
      ...(label !== 'STD' && { productTag: labelsGaNames[label] }),
    }),
    [elementBlock, product, productPosition, label]
  );

  const handleAddToCart = useCallback(() => {
    const step = product.price.step || 1;
    let initialSteps = Math.round(1 / step);
    if (step * initialSteps !== 1) {
      initialSteps += 1;
    }
    if (step > 1) {
      initialSteps = 1;
    }

    addToBasket(product.productId, step * initialSteps);
    prevQtyRef.current = step * initialSteps;

    analyticsUtils.sendEvent('GA:addToCart', {
      productQuantity: parseFloat((step * initialSteps).toFixed(2)).toString(),
      ...commonGaData,
      ...gfData,
    });
    dispatchDyAddToCart({
      id: product.productId,
      quantity: step * initialSteps,
      productPrice: product.price.main_price,
    });
  }, [addToBasket, commonGaData, gfData, product]);

  const handleUpdateCart = useCallback(
    (code: string, quantity: number) => {
      updateCartItem(code, quantity);
      analyticsUtils.sendEvent(
        quantity >= (prevQtyRef?.current || 0) ? 'GA:addToCart' : 'GA:removeFromCart',
        {
          productQuantity: parseFloat(Math.abs(quantity - prevQtyRef.current).toFixed(2)).toString(),
          ...commonGaData,
          ...updateGFData(gfData, product.productPriceCategory),
        }
      );
      dispatchDyAddToCart({
        id: product.productId,
        quantity,
        productPrice: product.price.main_price,
      });
      prevQtyRef.current = quantity;
    },
    [commonGaData, gfData, product, updateCartItem]
  );

  const handleRemoveFromCart = useCallback(() => {
    removeFromBasket(product.productId);
    prevQtyRef.current = 0;
  }, [product, removeFromBasket]);

  useIntersectionObserverCallback(
    productRef,
    {
      enter: () => {
        analyticsUtils.sendEvent('GA:impressions', {
          ...commonGaData,
          ...updateGFData(gfData, product.productPriceCategory),
        });
      },
      extraUnobserve: 'enter',
      options: { rootMargin: '-30px' },
    },
    [productPosition, gfData]
  );

  const onClick = useCallback(
    (elementName: 'productNameLink' | 'productPhotoLink' | 'productRatingLink') => {
      analyticsUtils.sendEvent('GA:clickProduct', {
        elementName,
        ...commonGaData,
        ...updateGFData(gfData, product.productPriceCategory),
      });
    },
    [commonGaData, gfData, product.productPriceCategory]
  );

  const onMouseDown = (e: MouseEvent) => {
    if (
      e.ctrlKey ||
      e.shiftKey ||
      e.metaKey || // for apple
      (e.button && e.button === 1) // middle/wheel click
    ) {
      if (rrContentId) clickRRTracking(rrContentId);
      return;
    }

    // disallow right click
    if (e.button === 2) return;

    if (rrContentId) clickRRTracking(rrContentId);
  };

  if (isOnScreen && rrContentId) viewRRTracking(rrContentId);

  return (
    <ProductCard
      className={className}
      product={product}
      ref={productRef}
      type={type}
      label={label}
      inBasket={inBasket}
      qty={qty}
      onClick={onClick}
      onMouseDown={onMouseDown}
      onCartAdd={handleAddToCart}
      onQtyUpdate={handleUpdateCart}
      onCartRemove={handleRemoveFromCart}
      reviews={review}
      inWidget={inWidget}
    />
  );
});

const mapStateToProps = (state: RootState, ownProps: ProductOwnProps) => {
  const basket = getBasket(state);
  const { product } = ownProps;
  const cartEntity = basket.find((e: { code: string }) => e.code === product.productId);
  const inBasket = !!cartEntity;
  const qty = cartEntity?.quantity;
  return { inBasket, qty };
};

export const mapDispatchToProps = (dispatch: Dispatch) => ({
  addToBasket: (code: string, quantity: number) => {
    dispatch(BasketFlowActions.UPDATE_ITEM({ quantity, code }));
  },
  updateCartItem: (code: string, quantity: number) =>
    dispatch(BasketFlowActions.UPDATE_ITEM({ quantity, code })),
  syncCartWithCookie: () => dispatch(BasketFlowActions.SYNC_CART_WITH_COOKIE()),
  removeFromBasket: (code: string) => {
    dispatch(BasketFlowActions.RESET_ITEM({ code }));
  },
});

export const Product = connect(mapStateToProps, mapDispatchToProps)(ProductLogic);
